import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import {
  CreditCard, Clock, Shield, Dumbbell, CalendarDays, Phone,
  MapPin, Mail, User, ChevronRight, AlertCircle, GraduationCap
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_e26bd618-434d-4121-947e-65674a4d52da/artifacts/twhikqt6_padel%20logo%20png%202.png";

const CAROUSEL_IMAGES = [
  { url: "https://customer-assets.emergentagent.com/job_court-booking-app-3/artifacts/mjjzffwx_2.jpg", alt: "Jugador de padel en cancha" },
  { url: "https://customer-assets.emergentagent.com/job_court-booking-app-3/artifacts/7abun8kt_10.jpg", alt: "Vista aérea de la cancha" },
  { url: "https://customer-assets.emergentagent.com/job_court-booking-app-3/artifacts/w32lcxzh_3.jpg", alt: "Jugador preparándose" },
  { url: "https://customer-assets.emergentagent.com/job_court-booking-app-3/artifacts/2t3d1toh_9.jpg", alt: "Jugador en acción nocturna" },
  { url: "https://customer-assets.emergentagent.com/job_court-booking-app-3/artifacts/0iky1hpv_4.jpg", alt: "Equipo de padel femenino" },
  { url: "https://customer-assets.emergentagent.com/job_court-booking-app-3/artifacts/j9nb03zm_7.jpg", alt: "Jugador con raqueta" },
  { url: "https://customer-assets.emergentagent.com/job_court-booking-app-3/artifacts/rtwqyekv_5.jpg", alt: "Jugadora con raqueta" },
  { url: "https://customer-assets.emergentagent.com/job_court-booking-app-3/artifacts/cqb6c1ej_8.jpg", alt: "Área de juegos infantil" },
  { url: "https://customer-assets.emergentagent.com/job_court-booking-app-3/artifacts/ig8oky4r_6.jpg", alt: "Jugador en acción" },
];

const TERMS = [
  { icon: CreditCard, title: "Pagos en Línea - Sin Devoluciones", desc: "Al elegir pagar en línea, aceptas que no hay cancelaciones ni devoluciones de dinero. El descuento del 15% se aplica precisamente por la garantía de tu reserva." },
  { icon: Clock, title: "Puntualidad", desc: "Te pedimos llegar 10 minutos antes de tu horario reservado. Si llegas tarde, el tiempo de juego no se extiende." },
  { icon: Dumbbell, title: "Renta de Equipo", desc: "Si no cuentas con material, no te preocupes. Contamos con renta de equipo disponible en el establecimiento." },
  { icon: Shield, title: "Uso de Instalaciones", desc: "El cliente es responsable del uso adecuado de las canchas y equipo. Cualquier daño intencional será cobrado al cliente." },
  { icon: CalendarDays, title: "Horarios de Operación", desc: "Lunes a Viernes: 6:00 am - 11:00 pm\nSábados: 6:00 am - 3:00 pm\nDomingos: Cerrado" },
];

export default function HomePage() {
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedCourt, setSelectedCourt] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [customer, setCustomer] = useState({ name: "", email: "", address: "", city: "", postal_code: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Calendar bounds: today to 2030
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const fromDate = today;
  const toDate = new Date(2030, 11, 31);

  // Disable Sundays and past dates
  const disabledDays = [{ dayOfWeek: [0] }, { before: today }];

  const fetchSlots = useCallback(async (date) => {
    setLoadingSlots(true);
    setSelectedSlot(null);
    setSelectedCourt(null);
    try {
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
      const { data } = await axios.get(`${API}/reservations/available-slots?date=${dateStr}`);
      
      // If selected date is today, disable slots that have already passed
      const now = new Date();
      const isToday = date.toDateString() === now.toDateString();
      const filteredSlots = (data.slots || []).map(slot => {
        if (isToday) {
          // Parse slot start time (e.g. "6:00 AM" from "6:00 AM - 7:30 AM")
          const startTime = slot.time.split(" - ")[0];
          const [time, period] = startTime.split(" ");
          const [hours, minutes] = time.split(":").map(Number);
          let slotHour = hours;
          if (period === "PM" && hours !== 12) slotHour += 12;
          if (period === "AM" && hours === 12) slotHour = 0;
          const slotDate = new Date(date);
          slotDate.setHours(slotHour, minutes, 0, 0);
          if (slotDate <= now) {
            return { ...slot, fully_booked: true, passed: true };
          }
        }
        return slot;
      });
      
      setSlots(filteredSlots);
    } catch (err) {
      toast.error("Error al cargar horarios");
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  }, []);

  useEffect(() => {
    if (selectedDate) fetchSlots(selectedDate);
  }, [selectedDate, fetchSlots]);

  const searchCustomer = async (email) => {
    if (email.length < 3) { setSuggestions([]); return; }
    try {
      const { data } = await axios.get(`${API}/customers/search?email=${encodeURIComponent(email)}`);
      setSuggestions(data);
      setShowSuggestions(data.length > 0);
    } catch { setSuggestions([]); }
  };

  const selectSuggestion = (c) => {
    setCustomer({ name: c.name || "", email: c.email || "", address: c.address || "", city: c.city || "", postal_code: c.postal_code || "", phone: c.phone || "" });
    setShowSuggestions(false);
  };

  const validateForm = () => {
    if (!selectedDate || !selectedSlot || !selectedCourt) {
      toast.error("Selecciona fecha, horario y cancha");
      return false;
    }
    const errors = {};
    if (!customer.name.trim()) errors.name = true;
    if (!customer.email.trim()) errors.email = true;
    if (!customer.address.trim()) errors.address = true;
    if (!customer.city.trim()) errors.city = true;
    if (!customer.postal_code.trim()) errors.postal_code = true;
    if (!customer.phone.trim()) errors.phone = true;
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error("Todos los campos del formulario son obligatorios");
      return false;
    }
    return true;
  };

  const createReservation = async () => {
    const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`;
    const slot = slots.find(s => s.time === selectedSlot);
    const onlinePrice = Math.round(slot.price * 0.85);
    const { data: reservation } = await axios.post(`${API}/reservations`, {
      date: dateStr, time_slot: selectedSlot, court: selectedCourt,
      price: onlinePrice, payment_type: "online", customer,
    });
    return reservation;
  };

  const handlePayMercadoPago = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      const reservation = await createReservation();
      const slot = slots.find(s => s.time === selectedSlot);
      const onlinePrice = Math.round(slot.price * 0.85);
      const { data: mpData } = await axios.post(`${API}/mercadopago/create-preference`, {
        reservation_id: reservation.id,
        title: `Reserva Padel - ${selectedSlot} - Cancha ${selectedCourt}`,
        price: onlinePrice, payer_email: customer.email,
      });
      if (mpData.init_point) { window.location.href = mpData.init_point; }
      else { toast.error("Error al crear el pago con Mercado Pago."); }
    } catch (err) { toast.error(err.response?.data?.detail || "Error al crear reserva"); }
    finally { setSubmitting(false); }
  };

  const handlePayStripe = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      const reservation = await createReservation();
      const { data: stripeData } = await axios.post(`${API}/stripe/create-checkout`, {
        reservation_id: reservation.id, origin_url: window.location.origin,
      });
      if (stripeData.url) { window.location.href = stripeData.url; }
      else { toast.error("Error al crear el pago con Stripe."); }
    } catch (err) { toast.error(err.response?.data?.detail || "Error al crear reserva"); }
    finally { setSubmitting(false); }
  };

  const handleSubmit = handlePayMercadoPago;

  const formatDateDisplay = (date) => {
    if (!date) return "";
    return date.toLocaleDateString("es-MX", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  };

  return (
    <div className="min-h-screen bg-white">
      <Toaster position="top-center" />

      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-zinc-200" data-testid="header">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-center">
          <img src={LOGO_URL} alt="Padel Club del Rincón" className="h-24 sm:h-28" data-testid="header-logo" />
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16" data-testid="hero-section">
        <div className="text-center mb-10">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tighter text-zinc-950 mb-4" data-testid="hero-title">
            <span className="block text-[#D6F32F] bg-zinc-950 inline-block px-4 py-1 mb-2 text-3xl sm:text-4xl">15% DE DESCUENTO</span>
            <span className="block">Padel Club del Rincón</span>
          </h1>
          <p className="text-base md:text-lg text-zinc-600 max-w-2xl mx-auto leading-relaxed">
            Reserva en línea y obtén un <strong className="text-zinc-950">15% de descuento</strong> automático. Selecciona tu fecha y horario favorito.
          </p>
        </div>

        {/* Classes CTA */}
        <div className="flex flex-col sm:flex-row justify-center gap-3 mb-8">
          <Link to="/clases?tab=personal" className="inline-flex items-center justify-center gap-2 bg-zinc-950 text-white px-6 py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors" data-testid="classes-personal-cta">
            <User className="w-5 h-5" /> Clases Personalizadas con Profesor
          </Link>
          <Link to="/clases?tab=clinic" className="inline-flex items-center justify-center gap-2 border-2 border-zinc-950 text-zinc-950 px-6 py-3 rounded-md font-medium hover:bg-zinc-50 transition-colors" data-testid="classes-clinic-cta">
            <GraduationCap className="w-5 h-5" /> Clínicas de Niños
          </Link>
        </div>

        {/* Carousel */}
        <div className="max-w-4xl mx-auto" data-testid="image-carousel">
          <Carousel opts={{ loop: true }} className="w-full">
            <CarouselContent>
              {CAROUSEL_IMAGES.map((img, i) => (
                <CarouselItem key={i}>
                  <div className="overflow-hidden rounded-xl">
                    <img src={img.url} alt={img.alt} className="w-full h-[300px] sm:h-[400px] md:h-[450px] object-cover" />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="left-2 sm:-left-12" />
            <CarouselNext className="right-2 sm:-right-12" />
          </Carousel>
        </div>
      </section>

      {/* Booking Section */}
      <section className="bg-zinc-50 py-16 md:py-24" data-testid="booking-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-950 text-center mb-12">
            Selecciona una Fecha
          </h2>
          <p className="text-sm text-zinc-500 text-center mb-8 uppercase tracking-widest font-bold">
            Calendario 2026 - 2030
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Calendar */}
            <Card className="border border-zinc-200 shadow-none" data-testid="calendar-card">
              <CardContent className="p-6 flex justify-center">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={disabledDays}
                  fromDate={fromDate}
                  toDate={toDate}
                  className="rounded-md"
                  data-testid="booking-calendar"
                />
              </CardContent>
            </Card>

            {/* Slots */}
            <Card className="border border-zinc-200 shadow-none" data-testid="slots-card">
              <CardContent className="p-6">
                {!selectedDate ? (
                  <div className="flex flex-col items-center justify-center h-full text-zinc-400 py-12">
                    <CalendarDays className="w-12 h-12 mb-4" />
                    <p className="text-sm">Selecciona una fecha para ver horarios</p>
                  </div>
                ) : loadingSlots ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-zinc-950 border-t-transparent" />
                  </div>
                ) : slots.length === 0 ? (
                  <div className="text-center py-12 text-zinc-500">
                    <AlertCircle className="w-12 h-12 mx-auto mb-4" />
                    <p>No hay horarios disponibles para esta fecha</p>
                  </div>
                ) : (
                  <>
                    <p className="text-sm font-medium text-zinc-700 mb-1">
                      {formatDateDisplay(selectedDate)}
                    </p>
                    <p className="text-xs text-zinc-400 mb-4">Selecciona un horario y cancha</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[400px] overflow-y-auto pr-1">
                      {slots.map((slot) => (
                        <button
                          key={slot.time}
                          disabled={slot.fully_booked}
                          onClick={() => { setSelectedSlot(slot.time); setSelectedCourt(null); }}
                          className={`slot-btn p-3 text-left border rounded-md transition-all text-sm ${
                            slot.fully_booked
                              ? "opacity-40 cursor-not-allowed bg-zinc-100 line-through"
                              : selectedSlot === slot.time
                              ? "selected border-zinc-950 border-2 bg-zinc-50"
                              : "border-zinc-200 bg-white hover:border-zinc-400"
                          }`}
                          data-testid={`slot-${slot.time.replace(/[:\s]/g, "-")}`}
                        >
                          <span className="font-medium text-zinc-900 block">{slot.time}</span>
                          <span className={`text-xs ${slot.is_discount ? "text-green-600" : "text-zinc-500"}`}>
                            ${slot.price} MXN
                          </span>
                          {slot.is_discount && !slot.passed && !slot.clinic_blocked && !slot.class_blocked && <span className="text-xs text-green-600 block">50% desc</span>}
                          {slot.passed && <span className="text-xs text-red-400 block">Horario pasado</span>}
                          {slot.clinic_blocked && <span className="text-xs text-blue-500 block">Clínica de niños</span>}
                          {slot.class_blocked && <span className="text-xs text-purple-500 block">Clase reservada</span>}
                        </button>
                      ))}
                    </div>

                    {/* Court Selection */}
                    {selectedSlot && (
                      <div className="mt-4 pt-4 border-t border-zinc-200 fade-in">
                        <p className="text-sm font-medium text-zinc-700 mb-2">Selecciona cancha:</p>
                        <div className="flex gap-3">
                          {slots.find(s => s.time === selectedSlot)?.available_courts.map((court) => (
                            <button
                              key={court}
                              onClick={() => setSelectedCourt(court)}
                              className={`flex-1 p-3 border rounded-md text-center transition-all ${
                                selectedCourt === court
                                  ? "border-2 border-zinc-950 bg-zinc-50 font-semibold"
                                  : "border-zinc-200 hover:border-zinc-400"
                              }`}
                              data-testid={`court-${court}`}
                            >
                              Cancha {court}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Booking Form */}
          {selectedSlot && selectedCourt && (
            <div className="max-w-2xl mx-auto mt-12 fade-in" data-testid="booking-form-section">
              <Card className="border border-zinc-200 shadow-none">
                <CardContent className="p-6 sm:p-8">
                  <h3 className="text-xl font-semibold text-zinc-950 mb-6">Datos del Cliente</h3>

                  {/* Price summary */}
                  <div className="bg-zinc-50 border border-zinc-200 rounded-md p-4 mb-6">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm text-zinc-600">Horario:</span>
                      <span className="font-medium text-zinc-900">{selectedSlot} - Cancha {selectedCourt}</span>
                    </div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm text-zinc-600">Precio normal:</span>
                      <span className="text-zinc-500 line-through">${slots.find(s => s.time === selectedSlot)?.price} MXN</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-zinc-600">Precio online (15% desc):</span>
                      <span className="font-bold text-zinc-950 text-lg">
                        ${Math.round((slots.find(s => s.time === selectedSlot)?.price || 0) * 0.85)} MXN
                      </span>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-zinc-700 mb-1 flex items-center gap-1">
                          <User className="w-3.5 h-3.5" /> Nombre del Cliente *
                        </label>
                        <Input
                          data-testid="customer-name"
                          value={customer.name}
                          onChange={(e) => { setCustomer({ ...customer, name: e.target.value }); setFormErrors({...formErrors, name: false}); }}
                          placeholder="Nombre completo"
                          required
                          className={`border-zinc-300 ${formErrors.name ? "border-red-500 ring-1 ring-red-500" : ""}`}
                        />
                      </div>
                      <div className="relative">
                        <label className="text-sm font-medium text-zinc-700 mb-1 flex items-center gap-1">
                          <Mail className="w-3.5 h-3.5" /> Correo Electrónico *
                        </label>
                        <Input
                          data-testid="customer-email"
                          type="email"
                          value={customer.email}
                          onChange={(e) => {
                            setCustomer({ ...customer, email: e.target.value });
                            setFormErrors({...formErrors, email: false});
                            searchCustomer(e.target.value);
                          }}
                          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                          placeholder="correo@ejemplo.com"
                          required
                          className={`border-zinc-300 ${formErrors.email ? "border-red-500 ring-1 ring-red-500" : ""}`}
                        />
                        {showSuggestions && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-zinc-200 rounded-md shadow-lg max-h-40 overflow-y-auto" data-testid="email-suggestions">
                            {suggestions.map((s, i) => (
                              <button
                                key={i}
                                type="button"
                                onClick={() => selectSuggestion(s)}
                                className="w-full text-left px-4 py-2 text-sm hover:bg-zinc-50 border-b border-zinc-100 last:border-0"
                              >
                                <span className="font-medium">{s.name}</span>
                                <span className="text-zinc-500 ml-2">{s.email}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-zinc-700 mb-1 flex items-center gap-1">
                          <Phone className="w-3.5 h-3.5" /> Número de Contacto *
                        </label>
                        <Input
                          data-testid="customer-phone"
                          value={customer.phone}
                          onChange={(e) => { setCustomer({ ...customer, phone: e.target.value }); setFormErrors({...formErrors, phone: false}); }}
                          placeholder="(+52) 123 456 7890"
                          required
                          className={`border-zinc-300 ${formErrors.phone ? "border-red-500 ring-1 ring-red-500" : ""}`}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-zinc-700 mb-1 flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5" /> Dirección *
                        </label>
                        <Input
                          data-testid="customer-address"
                          value={customer.address}
                          onChange={(e) => { setCustomer({ ...customer, address: e.target.value }); setFormErrors({...formErrors, address: false}); }}
                          placeholder="Dirección"
                          required
                          className={`border-zinc-300 ${formErrors.address ? "border-red-500 ring-1 ring-red-500" : ""}`}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-zinc-700 mb-1">Ciudad *</label>
                        <Input
                          data-testid="customer-city"
                          value={customer.city}
                          onChange={(e) => { setCustomer({ ...customer, city: e.target.value }); setFormErrors({...formErrors, city: false}); }}
                          placeholder="Ciudad"
                          required
                          className={`border-zinc-300 ${formErrors.city ? "border-red-500 ring-1 ring-red-500" : ""}`}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-zinc-700 mb-1">Código Postal *</label>
                        <Input
                          data-testid="customer-postal"
                          value={customer.postal_code}
                          onChange={(e) => { setCustomer({ ...customer, postal_code: e.target.value }); setFormErrors({...formErrors, postal_code: false}); }}
                          placeholder="C.P."
                          required
                          className={`border-zinc-300 ${formErrors.postal_code ? "border-red-500 ring-1 ring-red-500" : ""}`}
                        />
                      </div>
                    </div>

                    <Separator className="my-4" />

                    <div className="bg-zinc-50 border border-zinc-200 rounded-md p-4 text-sm text-zinc-600">
                      <p className="font-medium text-zinc-800 mb-1">Pago en línea (15% descuento adicional)</p>
                      <p>Elige tu método de pago preferido. Para pagos en efectivo, contactar directamente al club.</p>
                    </div>

                    <p className="text-xs text-zinc-500">
                      Al realizar una reserva, aceptas nuestros términos y condiciones.
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Button
                        type="button"
                        disabled={submitting}
                        onClick={handlePayMercadoPago}
                        className="w-full bg-[#009EE3] text-white hover:bg-[#0084C1] py-3 text-sm font-semibold"
                        data-testid="pay-mercadopago-btn"
                      >
                        {submitting ? (
                          <span className="flex items-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                            Procesando...
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <CreditCard className="w-4 h-4" /> Mercado Pago
                          </span>
                        )}
                      </Button>
                      <Button
                        type="button"
                        disabled={submitting}
                        onClick={handlePayStripe}
                        className="w-full bg-zinc-950 text-white hover:bg-zinc-800 py-3 text-sm font-semibold"
                        data-testid="pay-stripe-btn"
                      >
                        {submitting ? (
                          <span className="flex items-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                            Procesando...
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            Apple Pay / Tarjeta
                          </span>
                        )}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </section>

      {/* Terms & Conditions */}
      <section className="py-16 md:py-24" data-testid="terms-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-950 text-center mb-12">
            Términos y Condiciones
          </h2>
          <div className="space-y-6">
            {TERMS.map((term, i) => (
              <div key={i} className="flex gap-4 items-start" data-testid={`term-${i}`}>
                <div className="flex-shrink-0 w-10 h-10 bg-zinc-100 rounded-md flex items-center justify-center">
                  <term.icon className="w-5 h-5 text-zinc-700" />
                </div>
                <div>
                  <h3 className="font-semibold text-zinc-900 mb-1">{term.title}</h3>
                  <p className="text-sm text-zinc-600 whitespace-pre-line">{term.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <p className="text-zinc-600 text-base font-medium">
              Gracias por reservar en Padel Club del Rincón, nos vemos día de tu reserva.
            </p>
            <p className="text-xs text-zinc-400 mt-2">
              Al realizar una reserva, aceptas nuestros términos y condiciones.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-200 py-8" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <Link
            to="/admin"
            className="text-sm text-zinc-400 hover:text-zinc-600 transition-colors"
            data-testid="admin-link"
          >
            Administrador
          </Link>
        </div>
      </footer>
    </div>
  );
}
